import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { newToastId } from '@/lib/ids'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number
  action?: ToastAction
}

interface ToastContextValue {
  toasts: Toast[]
  showToast: (
    message: string,
    type?: ToastType,
    duration?: number,
    action?: ToastAction,
  ) => string
  updateToast: (id: string, updates: Partial<Omit<Toast, 'id'>>) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const showToast = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      duration: number = 5000,
      action?: ToastAction,
    ) => {
      const id = newToastId()
      const toast: Toast = { id, message, type, duration, action }

      setToasts((prev) => [...prev, toast])

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id)
        }, duration)
      }

      return id
    },
    [removeToast]
  )

  const updateToast = useCallback((id: string, updates: Partial<Omit<Toast, 'id'>>) => {
    setToasts((prev) =>
      prev.map((toast) => (toast.id === id ? { ...toast, ...updates } : toast)),
    )
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, showToast, updateToast, removeToast }}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
        role="region"
        aria-label="Notifications"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              min-w-[300px] max-w-md p-4 rounded-lg shadow-lg
              ${
                toast.type === 'error'
                  ? 'bg-red-600 text-white'
                  : toast.type === 'success'
                    ? 'bg-green-600 text-white'
                    : toast.type === 'warning'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-navy-light text-white border border-neon-cyan/30'
              }
            `}
            role="alert"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-1 flex-col gap-2">
                <p>{toast.message}</p>
                {toast.action ? (
                  <button
                    onClick={() => {
                      toast.action?.onClick()
                    }}
                    className="self-start rounded bg-white/10 px-3 py-1 text-sm text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
                  >
                    {toast.action.label}
                  </button>
                ) : null}
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                className="text-white/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
                aria-label="Dismiss notification"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
